import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, reactive, ref } from "vue";
  import useVuelidate from "@vuelidate/core";
  import { required, email, helpers } from "@vuelidate/validators";
  import { phoneNumberOption } from "@/helpers/cleavePlugin";
  import { validOptionalPhoneNumber } from "@/helpers/validation";
  import Cleave from "vue-cleave-component";
  import { useStore } from "vuex";
  import { useToast } from "vue-toastification";
  import { resetVuelidateErrors } from "@/helpers/vuelidate";
  import { StatesEnum } from "@/enums/StatesEnum";
  import DropDown from "@/components/ui/dropdown/DropDown.vue";
  import ImageCropperModal from "@/components/ui/modals/ImageCropperModal.vue";
  import { useRouter } from "vue-router";
  import { UserRoles } from "@/enums/UserRoles";
  import { UserInterface } from "@/types/UserInterface";

  interface CustomerOption {
    detailsId: string;
    companyName: string;
    adminEmail: string;
    landownerIds: string[];
  }

  
export default /*@__PURE__*/_defineComponent({
  __name: 'AdminBuilderCreate',
  setup(__props, { expose: __expose }) {
  __expose();

  const toast = useToast();
  const store = useStore();

  const loading = ref(false);
  const customerOptions = ref<CustomerOption[]>([]);
  const isCropperModalOpen = ref(false);
  const imageForCropper = ref("");
  const router = useRouter();

  const formFields = {
    name: "",
    email: "",
    phone: "",
    contact: "",
    address: "",
    logo: "",
    state: "",
    imageFile: {} as { data: string | ArrayBuffer; buffer: File },
    color: "#8f00ff",
    colorB: "#ffffff",
    colorC: "#f1f5f5",
    landownerIds: [] as string[],
  };

  const form = reactive(Object.assign({}, formFields));
  const selectedCustomersCount = computed(() => {
    return customerOptions.value.filter(isCustomerSelected).length;
  });

  const rules = {
    name: { required },
    email: { email },
    phone: {
      validPhoneNumber: helpers.withMessage(
        "Value must be valid phone number",
        validOptionalPhoneNumber
      ),
    },
    contact: {},
    address: {},
    state: {},
    color: { required },
    colorB: { required },
    colorC: { required },
    landownerIds: {},
  };

  const v$ = useVuelidate(rules, form);
  const uploadImage = (e: InputEvent) => {
    const files = (e.target as HTMLInputElement).files as FileList;

    if (!files) {
      return;
    }

    const reader = new FileReader();

    reader.onload = () => {
      imageForCropper.value = reader.result as string;
      isCropperModalOpen.value = true;
    };
    reader.readAsDataURL(files[0]);
  };
  const clearForm = () => {
    Object.assign(form, formFields);
    form.landownerIds = [];
    resetVuelidateErrors(v$);
  };

  const onCropImage = (file: File) => {
    form.imageFile = {
      data: URL.createObjectURL(file),
      buffer: file,
    };
    isCropperModalOpen.value = false;
  };

  const uploadImageRequest = async () => {
    const formData = new FormData();
    const logoFile = form.imageFile;

    if (!logoFile.buffer) {
      toast.error("Please upload a logo");
      return null;
    }

    formData.append("file", logoFile.buffer);

    try {
      const res = await store.dispatch("builder/uploadLogo", formData);

      return res.data;
    } catch (error) {
      toast.error("An error occurred while uploading the logo");
      return null;
    }
  };

  const validateForm = async () => {
    const isValid = await v$.value.$validate();

    return isValid;
  };
  const onCreateBuilder = async () => {
    const isValidForm = await validateForm();

    if (!isValidForm) {
      return;
    }

    loading.value = true;

    const logo = await uploadImageRequest();

    if (!logo) {
      loading.value = false;
      return;
    }

    try {
      await store.dispatch("builder/createBuilder", {
        ...form,
        logo,
      });
      toast.success("The builder was successfully created");
    } catch (error) {
      toast.error("An error occurred while creating the builder");
    } finally {
      loading.value = false;
      clearForm();
    }
  };

  function getLandowners() {
    store
      .dispatch("user/getUsers", {
        role: UserRoles.LANDOWNER,
      })
      .then((res) => {
        customerOptions.value = mapCustomers(res.data);
      })
      .catch(() => {
        toast.error("Failed to load customers");
      });
  }

  function mapCustomers(users: UserInterface[]): CustomerOption[] {
    const companyMap = new Map<string, CustomerOption>();

    users.forEach((user) => {
      const details = user.detailsId;

      if (!details || typeof details === "string" || !details._id) return;

      if (!companyMap.has(details._id)) {
        const companyName =
          "companyName" in details ? details.companyName : "Unknown Company";

        companyMap.set(details._id, {
          detailsId: details._id,
          companyName,
          adminEmail: user.email,
          landownerIds: [],
        });
      }

      companyMap.get(details._id)?.landownerIds.push(user._id);
    });

    return Array.from(companyMap.values());
  }

  function isCustomerSelected(customer: CustomerOption) {
    return customer.landownerIds.every((id) => form.landownerIds.includes(id));
  }

  function toggleCustomer(customer: CustomerOption) {
    if (isCustomerSelected(customer)) {
      form.landownerIds = form.landownerIds.filter(
        (id) => !customer.landownerIds.includes(id)
      );
      return;
    }

    form.landownerIds = Array.from(
      new Set([...form.landownerIds, ...customer.landownerIds])
    );
  }

  function checkAllCustomers() {
    form.landownerIds = Array.from(
      new Set(
        customerOptions.value.flatMap((customer) => customer.landownerIds)
      )
    );
  }

  function uncheckAllCustomers() {
    form.landownerIds = [];
  }

  onMounted(() => {
    getLandowners();
  });

const __returned__ = { toast, store, loading, customerOptions, isCropperModalOpen, imageForCropper, router, formFields, form, selectedCustomersCount, rules, v$, uploadImage, clearForm, onCropImage, uploadImageRequest, validateForm, onCreateBuilder, getLandowners, mapCustomers, isCustomerSelected, toggleCustomer, checkAllCustomers, uncheckAllCustomers, computed, onMounted, reactive, ref, get useVuelidate() { return useVuelidate }, get required() { return required }, get email() { return email }, get helpers() { return helpers }, get phoneNumberOption() { return phoneNumberOption }, get validOptionalPhoneNumber() { return validOptionalPhoneNumber }, get Cleave() { return Cleave }, get useStore() { return useStore }, get useToast() { return useToast }, get resetVuelidateErrors() { return resetVuelidateErrors }, get StatesEnum() { return StatesEnum }, DropDown, ImageCropperModal, get useRouter() { return useRouter }, get UserRoles() { return UserRoles }, get UserInterface() { return UserInterface } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})